import './index.sass';
import 'isomorphic-fetch';
import App, { AppInitialProps } from 'next/app';
import Head from 'next/head';
import { AppProvider } from '@shopify/polaris';
import { Provider, useAppBridge } from '@shopify/app-bridge-react';
import translations from '@shopify/polaris/locales/en.json';
import { AppDiContainerService, AppLogger, Guid } from '@devex/devex-shared';
import { AppInsightsWebLogger } from '@devex/devex-web-shared';
import React from 'react';
import { ToastContainer } from 'react-toastify';
import { NextPageContext } from 'next';
import { HttpService } from '../services/http-service';
import { ShippingZonesService } from '../services/shipping-zones-service';
import { ShippingRulesService } from '../services/shipping-rules-service';
import { SettingsService } from '../services/settings-service';
import { HelpService } from '../services/help-service';
import { StatesAndProvincesService } from '../services/states-and-provinces-service';
import Mail from '../components/tailwind/mail';
import 'react-toastify/dist/ReactToastify.css';
import { ConfigService } from '../services/config-service';

const configService = new ConfigService();
const httpService = new HttpService(configService);

AppDiContainerService.instance.addServiceByName(ConfigService.$name, configService);
AppDiContainerService.instance.addServiceByName(HttpService.id, httpService);
AppDiContainerService.instance.addServiceByName(ShippingZonesService.id, new ShippingZonesService(httpService));
AppDiContainerService.instance.addServiceByName(ShippingRulesService.id, new ShippingRulesService(httpService));
AppDiContainerService.instance.addServiceByName(SettingsService.id, new SettingsService(httpService));
AppDiContainerService.instance.addServiceByName(HelpService.id, new HelpService(httpService));
AppDiContainerService.instance.addServiceByName(StatesAndProvincesService.id, new StatesAndProvincesService(httpService));

function MyProvider(props: any) {
	const app = useAppBridge();

	HttpService.initialize(app);

	const { Component } = props;

	return (
		<Component {...props} />
	);
}

class MyApp extends App<{ pageProps: { host } }> {
	private static _aiLoaded = false;

	private _shopifyKey = '';

	public constructor(props: any) {
		super(props);

		this._shopifyKey = configService.getShopifyDetails().apiKey;
	}

	static async getInitialProps({ ctx }: { ctx: NextPageContext }): Promise<AppInitialProps<{ host }>> {
		const host = ctx.query.host;

		const pageProps: AppInitialProps<{ host }> = {
			pageProps: { host },
		};

		return pageProps;
	}

	public componentDidMount() {
		if (!MyApp._aiLoaded) {
			MyApp._aiLoaded = true;

			const aiConfig = configService.getAppInsightsDetails();

			AppLogger.add(new AppInsightsWebLogger(aiConfig.apiKey, Guid.fromString(aiConfig.clientId)));

			this._shopifyKey = configService.getShopifyDetails().apiKey;
		}
	}

	render() {
		const {
			Component,
			pageProps,
		} = this.props;
		const host: string = pageProps.host || '';

		const config = {
			apiKey: this._shopifyKey,
			host,
			forceRedirect: true,
		};

		console.log(`Using host: '${config.host}'`);

		return (
			<>
				<Head>
					<title>Shipping Rates Logic</title>
					<meta charSet="utf-8" />
				</Head>
				<Provider config={config}>
					<AppProvider i18n={translations}>
						<MyProvider Component={Component} {...pageProps} host={host} />
						<ToastContainer
							position="bottom-center"
							autoClose={5000}
							newestOnTop
							closeOnClick
							rtl={false}
							pauseOnFocusLoss={false} />
					</AppProvider>
				</Provider>
				<Mail />
			</>
		);
	}
}

export default MyApp;
